<template>
<div class="flex flex-col justify-center items-center">
  <section class="flex flex-col">
    <section class="mt-10 flex md:flex-row flex-col justify-center items-center mx-6 sm:mx-0" :aria-label="$t('order.product')">
      <div class="sm:w-1/2 sm:self-start">
        <inner-image-zoom :fullscreenOnMobile="true" :src="selectedProduct.cover ? selectedProduct.cover : 'undefined'" :alt="getAltText(selectedProduct.description)"/>
      </div>
      <div class="sm:ml-6 w-full mt-4 sm:mt-0">
        <h2 class="flex flex-col" :class="fontHairline()" v-if="selectedProduct.product_brand">
          {{selectedProduct.product_brand.name}}
        </h2>
        <h1 class="font-extrabold text-3xl">
          {{selectedProduct.description}}
        </h1>
        <section class="flex flex-row mt-2 w-full border-t divide-gray-400"
          :aria-label="$t('product.price')"
          :class="selectedProduct.out_of_stock ? 'justify-end' : 'justify-between'"
          >
          <section class="mt-5" :aria-label="$t('product.quantity')" v-show="!selectedProduct.out_of_stock">
            <h2 class="text-sm text-gray-600">
              {{$t('product.quantity')}}
            </h2>
            <inc-dec class="h-10 border rounded-md font-bold text-xl"
              :app-options="{
                  min: selectedProduct.min_quantity,
                  max: selectedProduct.max_purchasable_quantity
                }"
              v-model="qty"
              />
          </section>
          <section class="relative m-4 mr-12" v-if="showWhyDiscount">
            <div class="p-4 rounded px-6 bg-yellow-500 flex justify-center w-40 absolute" >
              <h4> {{$t('notifications.whyDiscount')}} {{ `${this.customer.customer_group.name}`}} </h4>
            </div>
          </section>
          <section class="mt-8 flex flex-col text-right" :aria-label="$t('product.price')">
            <h2 class="text-3xl h-10 font-sans-serif text-black" v-if="!selectedProduct.is_discounted">
              {{formatter.format(priceToShow /100)}}
            </h2>
            <div @mouseover="setShowWhyDiscount()" @mouseout="setShowWhyDiscount()" v-else>
              <span class="text-b2c-red-300 line-through">
                <h2 class="text-3xl h-10 font-sans-serif text-black">
                  {{formatter.format(priceToShow /100)}}
                </h2>
              </span>

              <h3 class="text-3xl h-10 font-sans-serif text-b2c-red-300">
                {{formatter.format(discountedPriceToShow / 100) }}
              </h3>
            </div>

            <h3 class="text-gray-600 text-xs" v-if="isTaxable">
              {{ $t('product.vat') }}
            </h3>
            <h3 class="text-gray-600 text-xs" v-else>
              {{ $t('product.noVat') }}
            </h3>
          </section>
        </section>
        <div class="w-full flex justify-end mt-6">
          <add-to-cart-button v-if="!selectedProduct.out_of_stock"
            class="mb-6 lg:w-2/5 w-1/2"
            :product="selectedProduct"
            :quantity="qty">
          </add-to-cart-button>
          <div v-if="selectedProduct.out_of_stock" class="w-full flex justify-end">
            <span v-if="!selectedProduct.available_on_request"
              class="p-4 text-b2c-red-300 text-2xl italic">
                {{ $t('product.outOfStock') }}
            </span>
            <div v-else class="w-full flex flex-col items-end mb-6">
              <div class="w-full flex justify-end items-center">
                <on-request-button  class="lg:w-2/5 md:w-1/2 w-3/4" :product="selectedProduct" />
                <button class="font-bold rounded-full w-5 ml-3 bg-gray-300 text-gray-600 text-sm
                  transform hover:-translate-y-1 duration-200"
                  @mouseover="showMessage=true" @mouseout="showMessage=false" @click="showMessage=!showMessage">
                    ?
                </button>
              </div>
              <span v-if="showMessage" class="text-xs mt-3 italic">
                {{ $t('product.onRequestMessage') }}
              </span>
            </div>

          </div>

        </div>
      </div>
    </section>

    <product-bottom-icons />
  </section>

  <section class="w-full h-auto bg-b2c-grey-100 font-sans-serif mt-6 text-left py-8 px-10" :aria-label="$t('product.widget')">
    <div class="flex flex-col sm:flex-row mb-10">
      <h1 class="text-b2c-red-300 font-bold text-2xl sm:w-1/2">
      {{ $t('product.details') }}
      </h1>
      <section class="sm:w-1/2 leading-5 mt-2" id="elencoAttributi" v-if="selectedProduct.product_category" :aria-label="$t('product.details')">
        <section aria-label="Extended description" class="pb-4 text-justify">
          {{ selectedProduct.extended_description }}
        </section>
        <section v-if="selectedProduct.product_attributes">
          <product-attributes :attributes="selectedProduct.attributes" :attribute_tags="selectedProduct.product_category.attribute_tags" />
        </section>

      </section>
    </div>
    <div class="flex flex-col sm:flex-row">
      <h1 class="text-b2c-red-300 font-bold text-2xl sm:w-1/2">
      {{ $t('product.delivery') }}
      </h1>
      <h2 class="sm:w-1/2 leading-5 mt-2 text-justify">
        {{$t('product.free-delivery')}}
      </h2>
    </div>
  </section>

  <div class="flex items-center w-screen md:w-full my-6">
    <carousel-fixed />
  </div>

</div>
</template>

<script>
import IncDec from '@/components/IncDec.vue';
import AddToCartButton from '@/components/AddToCartButton.vue';
import { mapGetters } from 'vuex';
import { getProduct } from '@/services/ProductService';
import CarouselFixed from '@/components/graphics/CarouselFixed.vue';
import ProductBottomIcons from '@/components/graphics/ProductBottomIcons.vue';
import ProductAttributes from '@/components/ProductAttributes.vue';
import 'vue-inner-image-zoom/lib/vue-inner-image-zoom.css';
import InnerImageZoom from 'vue-inner-image-zoom';
import OnRequestButton from '../components/OnRequestButton.vue';

export default {

  components: {
    IncDec,
    AddToCartButton,
    CarouselFixed,
    ProductBottomIcons,
    ProductAttributes,
    OnRequestButton,
    'inner-image-zoom': InnerImageZoom,
  },

  data() {
    return {
      selectedProduct: {},
      qty: 1,
      sku: this.$route.params.sku,
      showWhyDiscount: false,

      showMessage: false,
    };
  },

  beforeRouteUpdate(to, from, next) {
    this.sku = to.params.sku;
    next();
  },

  watch: {
    changedLocale() {
      this.selectedProduct = {};
      this.getSelectedProduct();
    },
    sku() {
      this.selectedProduct = {};
      this.getSelectedProduct();
    },
  },

  computed: {
    ...mapGetters({
      changedLocale: 'auth/changedLocale',
      customer: 'auth/customer',
      isTaxable: 'auth/isTaxable',
    }),

    isString() {
      return this.selectedProduct.product_category.slug === 'STR';
    },

    discountedPriceToShow() {
      if (!this.isTaxable) {
        return this.selectedProduct.discounted_price;
      }
      return this.selectedProduct.discounted_price_with_vat;
    },

    priceToShow() {
      if (!this.isTaxable) {
        return this.selectedProduct.price;
      }
      return this.selectedProduct.price_with_vat;
    },

    available() {
      return this.selectedProduct.ava;
    },

  },

  created() {
    this.getSelectedProduct();
  },

  methods: {
    getAltText(productDescription) {
      return `${this.$t('product.alt')} ${productDescription}`;
    },

    getSelectedProduct() {
      getProduct(this.sku).then(({ data }) => {
        this.selectedProduct = data;
        this.qty = this.selectedProduct.min_quantity;
      }).catch(() => {
        this.createNotification('error', `${this.$t('notifications.errorReload')}`);
      });
    },

    goToConfigurator() {
      this.$router.push('/configurator');
    },

    setShowWhyDiscount() {
      this.showWhyDiscount = !this.showWhyDiscount;
    },
  },
};
</script>
